import React, { useEffect, useState, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Separator } from '../../components/ui/separator';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from '../../components/ui/pagination';
import axios from 'axios';
import { toast } from "sonner";

const Reviews = () => {
    const token = localStorage.getItem("adminToken");
    const [reviews, setReviews] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // Fetch reviews data
    useEffect(() => {
        const fetchReviews = async () => {
            setIsLoading(true);
            try {
                const res = await axios.get('https://wavetechservices.in/api/admin/reviews', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setReviews(res.data.data);
            } catch (error) {
                console.error("Error fetching reviews:", error);
                toast.error("Failed to fetch reviews");
            } finally {
                setIsLoading(false);
            }
        };
        fetchReviews();
    }, [token]);

    // Filter reviews based on search term
    const filteredReviews = useMemo(() => {
        if (!searchTerm) return reviews;
        
        const lowercasedSearch = searchTerm.toLowerCase();
        return reviews.filter(review => 
            review.user?.name?.toLowerCase().includes(lowercasedSearch) ||
            review.subService?.name?.toLowerCase().includes(lowercasedSearch) ||
            review.comment?.toLowerCase().includes(lowercasedSearch) ||
            review.status?.toLowerCase().includes(lowercasedSearch) ||
            review.rating?.toString().includes(searchTerm) ||
            review.date?.slice(0, 10).includes(searchTerm)
        );
    }, [reviews, searchTerm]);

    const handleStatusUpdate = async (reviewId, status) => {
        try {
            setIsLoading(true);
            const res = await axios.put(
                `https://wavetechservices.in/api/admin/reviews/${reviewId}/status`,
                { status },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success(res.data.message);
            
            // Update state after status change
            setReviews(prevReviews => prevReviews.map(review =>
                review._id === reviewId ? { ...review, status } : review
            ));
        } catch (error) {
            console.error("Error updating review status:", error);
            toast.error(error.response?.data?.message || "Failed to update review status");
        } finally {
            setIsLoading(false);
        }
    };

    console.log("reviews : " , reviews)

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    // Calculate pagination based on filtered reviews
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredReviews.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredReviews.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Reset to first page when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString();
    };

    return (
        <div className='px-[1vw] my-5'>
            <div className="flex justify-between items-center">
                <h1 className='text-2xl'>Reviews ({filteredReviews.length})</h1>
                <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input 
                        placeholder="Search reviews..."
                        className="pl-10"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>
            <Separator className='my-4' />

            <div className='overflow-x-auto lg:w-full w-full h-[65vh]'>
                {isLoading ? (
                    <div className="flex justify-center items-center h-full">
                        <p>Loading reviews...</p>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[50px]">Sl No.</TableHead>
                                <TableHead className="min-w-[100px]">Date</TableHead>
                                <TableHead>Customer</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Service</TableHead>
                                <TableHead>Rating</TableHead>
                                <TableHead className="min-w-[200px]">Comment</TableHead>
                                <TableHead className="min-w-[150px]">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {currentItems.length > 0 ? (
                                currentItems.map((review, index) => (
                                    <TableRow key={review._id}>
                                        <TableCell>{indexOfFirstItem + index + 1}.</TableCell>
                                        <TableCell>{formatDate(review?.createdAt?.slice(0 , 10))}</TableCell>
                                        <TableCell>{review.user?.name || "N/A"}</TableCell>
                                        <TableCell>
                                            <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                                                review.status === 'pending' ? 'bg-orange-100 text-orange-800' :
                                                review.status === 'approved' ? 'bg-green-100 text-green-800' : 
                                                'bg-red-100 text-red-800'
                                            }`}>
                                                {review.status?.charAt(0).toUpperCase() + review.status?.slice(1) || "N/A"}
                                            </span>
                                        </TableCell>
                                        <TableCell>{review.subService?.name || "N/A"}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center">
                                                {review.rating}
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="h-4 w-4 ml-1 text-yellow-500"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                >
                                                    <path
                                                        d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                                                    />
                                                </svg>
                                            </div>
                                        </TableCell>
                                        <TableCell className="truncate max-w-[200px]">
                                            {review.comment || "N/A"}
                                        </TableCell>
                                        <TableCell className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                className="text-green-600 border-green-600 hover:bg-green-50"
                                                onClick={() => handleStatusUpdate(review._id, "approved")}
                                                disabled={review.status === "approved" || isLoading}
                                            >
                                                Approve
                                            </Button>
                                            <Button
                                                variant="outline"
                                                className="text-red-600 border-red-600 hover:bg-red-50"
                                                onClick={() => handleStatusUpdate(review._id, "rejected")}
                                                disabled={review.status === "rejected" || isLoading}
                                            >
                                                Reject
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center py-8">
                                        {searchTerm ? "No matching reviews found" : "No reviews available"}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </div>

            {filteredReviews.length > itemsPerPage && (
                <div className="mt-4">
                    <Pagination>
                        <PaginationContent>
                            <PaginationItem>
                                <PaginationPrevious 
                                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                                    disabled={currentPage === 1}
                                />
                            </PaginationItem>

                            {Array.from({ length: totalPages }).map((_, index) => {
                                const page = index + 1;
                                if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                                    return (
                                        <PaginationItem key={page}>
                                            <PaginationLink
                                                isActive={page === currentPage}
                                                onClick={() => handlePageChange(page)}
                                            >
                                                {page}
                                            </PaginationLink>
                                        </PaginationItem>
                                    );
                                }
                                return null;
                            })}

                            <PaginationItem>
                                <PaginationNext 
                                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                                    disabled={currentPage === totalPages}
                                />
                            </PaginationItem>
                        </PaginationContent>
                    </Pagination>
                </div>
            )}
        </div>
    );
};

export default Reviews;